import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// NudgeComposer — a small modal for poking a teammate about a task
// that's holding up the caller's work. Opened from the "Nudge" button
// on a waiting scheduler row. The nudge lands as an in-app notification
// for the recipient, carrying the project + task context so they see
// exactly what's being asked about.
import { useMemo, useState } from "react";
import { api } from "../api/client";
const MAX_LEN = 200;
// Quick-message chips — clicking one drops a ready-to-send note into the
// textarea. The user can still edit it before sending.
const QUICK_MESSAGES = [
    {
        label: "Any update on this?",
        text: "Any update on this? It's holding up my work — would love to get it moving this week.",
    },
    {
        label: "Can you prioritize this?",
        text: "Could you prioritize this when you get a chance? A couple of my tasks are waiting on it.",
    },
    {
        label: "This is blocking me",
        text: "Heads up — this is blocking me from moving forward. Anything I can do to help unblock it?",
    },
];
function buildOptions(blockers) {
    const out = [];
    for (const b of blockers) {
        for (const a of b.assignees) {
            out.push({
                key: `${b.task_id}:${a.id}`,
                taskId: b.task_id,
                taskTitle: b.title,
                projectName: b.project_name,
                userId: a.id,
                userName: a.name,
            });
        }
    }
    return out;
}
function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return "?";
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
export default function NudgeComposer({ open, blockers, onClose, onSent, }) {
    const options = useMemo(() => buildOptions(blockers), [blockers]);
    const [selectedKey, setSelectedKey] = useState(() => options[0]?.key ?? "");
    const [message, setMessage] = useState(QUICK_MESSAGES[0].text);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(false);
    if (!open)
        return null;
    const selected = options.find((o) => o.key === selectedKey) ?? options[0] ?? null;
    if (!selected) {
        // No one owns the blocking work — nothing to nudge. Render a small
        // explanatory modal instead of an empty composer.
        return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal nudge-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Nothing to nudge yet" }), _jsx("p", { className: "muted small", children: "The task that's holding this up doesn't have an owner assigned yet \u2014 there's no one to nudge. Ask an admin or manager to assign it first." }), _jsx("div", { className: "modal-actions", children: _jsx("button", { onClick: onClose, className: "btn-primary", children: "Got it" }) })] }) }));
    }
    async function send() {
        if (!selected)
            return;
        const note = message.trim();
        if (!note) {
            setError("Add a short message to your nudge.");
            return;
        }
        setSending(true);
        setError(null);
        try {
            await api.post("/notifications/nudge", {
                recipient_id: selected.userId,
                task_id: selected.taskId,
                message: note,
            });
            setSent(true);
            onSent?.();
            // Brief "sent" confirmation, then close.
            window.setTimeout(onClose, 900);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't send that nudge.");
        }
        finally {
            setSending(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal nudge-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "nudge-head", children: [_jsx("span", { className: "nudge-avatar", "aria-hidden": "true", children: initials(selected.userName) }), _jsxs("div", { className: "nudge-head-body", children: [_jsxs("strong", { children: ["Nudge ", selected.userName] }), _jsx("span", { className: "muted small", children: "They'll get an in-app notification from you." })] })] }), options.length > 1 && (_jsxs("label", { className: "nudge-recipient-pick", children: ["Who needs the nudge?", _jsx("select", { value: selectedKey, onChange: (e) => setSelectedKey(e.target.value), children: options.map((o) => (_jsxs("option", { value: o.key, children: [o.userName, " \u2014 ", o.taskTitle] }, o.key))) })] })), _jsxs("div", { className: "nudge-context", children: [_jsx("span", { className: "muted small", children: "About" }), _jsx("span", { className: "nudge-context-task", children: selected.taskTitle }), selected.projectName && (_jsxs("span", { className: "muted small", children: ["\u00B7 ", selected.projectName] }))] }), _jsxs("div", { className: "nudge-quick", children: [_jsx("span", { className: "muted small", children: "Quick messages" }), _jsx("div", { className: "nudge-quick-chips", children: QUICK_MESSAGES.map((q) => (_jsx("button", { type: "button", className: `nudge-chip ${message === q.text ? "active" : ""}`, onClick: () => setMessage(q.text), children: q.label }, q.label))) })] }), _jsx("textarea", { className: "nudge-textarea", value: message, maxLength: MAX_LEN, rows: 3, onChange: (e) => setMessage(e.target.value), placeholder: "Add a short, friendly note\u2026" }), _jsxs("div", { className: "nudge-charcount muted small", children: [message.length, " / ", MAX_LEN] }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: sending, children: "Cancel" }), _jsx("button", { onClick: send, className: "btn-primary", disabled: sending || sent || message.trim().length === 0, children: sent ? "Nudge sent ✓" : sending ? "Sending…" : "Send nudge" })] })] }) }));
}
